import { Component, OnDestroy, OnInit, HostBinding, HostListener, ViewEncapsulation } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';

import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreMediaService } from '@core/services/media.service';

import { coreConfig } from 'app/app-config';
import { Router } from '@angular/router';
import { IUser } from 'app/interfaces/user.interface';
import { ActiveUser, isAdmin } from 'app/utils/helpers.utils';
import { AuthService } from 'app/services/auth/auth.service';
import { AcademicYearsService } from 'app/services/AcademicYears/academic-years.service';
import { IAcademicYear } from 'app/interfaces/academic-year.interface';
import { ToastrService } from 'ngx-toastr';
import { LocalStore } from 'app/utils/localstore.utils';
import { SemesterService } from 'app/services/semester/semester.service';
import { SequenceService } from 'app/services/sequence/sequence.service';
import { ISemester } from 'app/interfaces/semester';
import { ISequence } from 'app/interfaces/sequence';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit, OnDestroy {
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public currentUser: IUser = ActiveUser();
  public isAdmin: boolean = isAdmin();

  public academicYears: IAcademicYear[] = [];
  public selectedAcademicYear: IAcademicYear = this.currentUser?.academicYear;

  public semesters: ISemester[] = [];
  public selectedSemester: ISemester | null = null;
  public sequences: ISequence[] = [];
  public selectedSequence: ISequence | null = null;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;

  @HostBinding('class.fixed-top')
  public isFixed = false;

  @HostBinding('class.navbar-static-style-on-scroll')
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == 'navbar-static-top' &&
      this.coreConfig.layout.type == 'horizontal'
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMediaService} _coreMediaService
   * @param {MediaObserver} _mediaObserver
   * @param {TranslateService} _translateService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    public _translateService: TranslateService,
    private authService: AuthService,
    private academicYearService: AcademicYearsService,
    private semesterService: SemesterService,
    private sequenceService: SequenceService,
    private toastr: ToastrService
  ) {
    this.languageOptions = {
      en: {
        title: 'English',
        flag: 'us'
      },
      fr: {
        title: 'French',
        flag: 'fr'
      },
      de: {
        title: 'German',
        flag: 'de'
      },
      pt: {
        title: 'Portuguese',
        flag: 'pt'
      }
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);

    this._coreConfigService.setConfig({ app: { appLanguage: language } }, { emitEvent: true });
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem('prevSkin');

    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : 'default' } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: 'dark' } }, { emitEvent: true });
    }
  }

  goBack() {
    window.history.back();
  }

  /**
   * Logout method
   */

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to the config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === 'horizontal';
      this.hiddenMenu = config.layout.menu.hidden === true;
      this.currentSkin = config.layout.skin;

      // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
      if (this.coreConfig.layout.type === 'vertical') {
        setTimeout(() => {
          if (this.coreConfig.layout.navbar.type === 'fixed-top') {
            this.isFixed = true;
          }
        }, 0);
      }
    });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == 'horizontal') {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._mediaObserver.isActive('bs-gt-xl');
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang
    });

    if (this.isAdmin) {
      this.loadAcademicYears();
      this.loadSemesters();
    }
  }

  /**
   * Load academic years
   */
  loadAcademicYears(): void {
    this.academicYearService.allAcademicYears({ page: 1, limit: 100 }).subscribe({
      next: (response) => {
        if (response?.data) {
          this.academicYears = response.data.data || response.data;
          // Use user's stored academic year first, otherwise find the active one
          if (this.currentUser?.academicYear) {
            // Find the matching year from the list to ensure we have the latest data
            const userYear = this.academicYears.find((y) => y.id === this.currentUser.academicYear.id);
            this.selectedAcademicYear = userYear || this.currentUser.academicYear;
          } else {
            // Fall back to active year from API
            this.selectedAcademicYear = this.academicYears.find((y) => y.is_active);
          }
        }
      },
      error: (error) => {
        console.error('Error loading academic years:', error);
      },
    });
  }

  /**
   * Switch academic year
   * @param year 
   */
  switchAcademicYear(year: IAcademicYear): void {
    if (year.id === this.selectedAcademicYear?.id) return;

    this.academicYearService.setActiveAcademicYear(true, year.id.toString()).subscribe({
      next: () => {
        // Update user object in local storage with the new academic year
        const user = ActiveUser();
        if (user) {
          user.academicYear = year;
          user.academic_year_id = year.id;
          LocalStore.setItem('user', user);
        }

        this.toastr.success(`Switched to academic year: ${year.name}`, 'Success');
        // Reload to refresh all data context according to the new active year
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
      error: (error) => {
        this.toastr.error('Failed to switch academic year', 'Error');
        console.error('Error switching academic year:', error);
      }
    });
  }

  /**
   * Load semesters for current academic year
   */
  loadSemesters(): void {
    this.semesterService.allSemesters({ page: 1, limit: 100 }).subscribe({
      next: (response) => {
        if (response?.data) {
          this.semesters = response.data.data || response.data;
          // Find current semester
          this.selectedSemester = this.semesters.find((s) => s.is_current) || this.semesters[0];
          if (this.selectedSemester) {
            this.loadSequences();
          }
        }
      },
      error: (error) => {
        console.error('Error loading semesters:', error);
      },
    });
  }

  /**
   * Load sequences for current semester
   */
  loadSequences(): void {
    if (!this.selectedSemester?.id) return;

    this.sequenceService.getSequencesBySemester(this.selectedSemester.id).subscribe({
      next: (response) => {
        if (response?.data) {
          this.sequences = response.data.data || response.data;
          // Find current sequence
          this.selectedSequence = this.sequences.find((s) => s.is_current) || this.sequences[0];
        }
      },
      error: (error) => {
        console.error('Error loading sequences:', error);
      },
    });
  }

  /**
   * Switch semester
   */
  switchSemester(semester: ISemester): void {
    if (semester.id === this.selectedSemester?.id) return;

    this.semesterService.setActiveSemester(true, semester.id.toString()).subscribe({
      next: () => {
        // Update user object in local storage with the new semester
        const user = ActiveUser();
        if (user) {
          user.semester = semester;
          user.semester_id = semester.id;
          LocalStore.setItem('user', user);
        }

        this.toastr.success(`Switched to semester: ${semester.name}`, 'Success');
        // Reload to refresh all data context according to the new active semester
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
      error: (error) => {
        this.toastr.error('Failed to switch semester', 'Error');
        console.error('Error switching semester:', error);
      }
    });
  }

  /**
   * Switch sequence
   */
  switchSequence(sequence: ISequence): void {
    if (sequence.id === this.selectedSequence?.id) return;

    this.sequenceService.setActiveSequence(true, sequence.id.toString()).subscribe({
      next: () => {
        // Update user object in local storage with the new sequence
        const user = ActiveUser();
        if (user) {
          user.sequence = sequence;
          user.sequence_id = sequence.id;
          LocalStore.setItem('user', user);
        }

        this.toastr.success(`Switched to sequence: ${sequence.name}`, 'Success');
        // Reload to refresh all data context according to the new active sequence
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
      error: (error) => {
        this.toastr.error('Failed to switch sequence', 'Error');
        console.error('Error switching sequence:', error);
      }
    });
  }


  logout() {
    this.authService.logout()
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
